import { User, Plus, Trash2 } from 'lucide-react';
import { Section, Field, EditButton, EditActions } from '../ui';
import { ic, formatDate, formatDOBInput } from '../helpers';

// ─────────────────────────────────────────
// DRIVERS SECTION
// Additional household drivers (beyond the named insured).
// ─────────────────────────────────────────
export default function DriversSection({
  drivers,
  editingDriverId,
  driverDraft,
  setDriverDraft,
  saving,
  onAddDriver,
  onStartEditDriver,
  onSaveDriver,
  onCancelEditDriver,
  onDeleteDriver,
}) {
  return (
    <Section
      title={`Additional Drivers (${drivers.length})`}
      icon={User}
      action={
        <button
          onClick={onAddDriver}
          className="flex items-center gap-1 px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition"
        >
          <Plus size={11} /> Add Driver
        </button>
      }
    >
      <div className="space-y-3 pt-3">
        {drivers.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">
            No additional drivers. Add household members who may drive vehicles on this policy.
          </p>
        )}

        {drivers.map((driver) => {
          const isEditing = editingDriverId === driver.id;
          const draft = isEditing ? driverDraft : driver;
          const update = (key, val) => setDriverDraft(p => ({ ...p, [key]: val }));

          return (
            <div key={driver.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-blue-600" />
                  <span className="text-sm font-semibold text-slate-700">
                    {[driver.firstName, driver.lastName].filter(Boolean).join(' ') || 'New Driver'}
                  </span>
                  {driver.relationshipToInsured && (
                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-lg">
                      {driver.relationshipToInsured}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isEditing
                    ? <EditActions onSave={() => onSaveDriver(driver.id)} onCancel={onCancelEditDriver} saving={saving} />
                    : <>
                      <EditButton onClick={() => onStartEditDriver({ ...driver, dateOfBirth: formatDate(driver.dateOfBirth) || '' })} />
                      <button onClick={() => onDeleteDriver(driver.id)} className="p-1 text-slate-400 hover:text-red-500 transition">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                {isEditing ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">First Name</label>
                      <input value={draft.firstName || ''} onChange={e => update('firstName', e.target.value)} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Last Name</label>
                      <input value={draft.lastName || ''} onChange={e => update('lastName', e.target.value)} className={ic} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Date of Birth</label>
                      <input
                        type="text"
                        value={draft.dateOfBirth || ''}
                        onChange={e => update('dateOfBirth', formatDOBInput(e.target.value))}
                        placeholder="MM/DD/YYYY"
                        className={ic}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Gender</label>
                      <select value={draft.gender || ''} onChange={e => update('gender', e.target.value)} className={ic}>
                        <option value="">—</option>
                        {['Male','Female','Non-binary','Prefer not to say'].map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Relationship to Insured</label>
                      <select value={draft.relationshipToInsured || ''} onChange={e => update('relationshipToInsured', e.target.value)} className={ic}>
                        <option value="">—</option>
                        {['Spouse','Child','Parent','Sibling','Other Relative','Other'].map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Driver Status</label>
                      <select value={draft.driverStatus || 'Active'} onChange={e => update('driverStatus', e.target.value)} className={ic}>
                        {['Active','Excluded','Non-Driver'].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <Field label="First Name" value={driver.firstName} editing={false} />
                    <Field label="Last Name" value={driver.lastName} editing={false} />
                    <Field label="Date of Birth" value={driver.dateOfBirth} isDate={true} editing={false} />
                    <Field label="Gender" value={driver.gender} editing={false} />
                    <Field label="Relationship" value={driver.relationshipToInsured} editing={false} />
                    <Field label="Status" value={driver.driverStatus} editing={false} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}